import { ChevronRight as EndIconOpenInPlannerBtn } from "@mui/icons-material";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, To } from "react-router";

import { GqlPlannerVisitStatus } from "~/__generated__";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { dateForUrlFormatter, formatDate } from "~/modules/app/util/formatters";

import { ProjectReviewVisitV2RejectionAlert } from "../../ProjectReviewVisitV2RejectionAlert";
import { ProjectReviewVisitV2StatusHandlerFragment } from "./fragments.generated";
import { ProjectReviewVisitV2FieldworkersWithoutReportList } from "./ProjectReviewVisitV2FieldworkersWithoutReportList";

type ProjectReviewVisitV2StatusHandlerProps = {
    visit: ProjectReviewVisitV2StatusHandlerFragment;
};

export function ProjectReviewVisitV2StatusHandler({ visit }: ProjectReviewVisitV2StatusHandlerProps) {
    const date = visit.startTime ? parseStringToDate(visit.startTime) : new Date();
    const openInPlannerUrl = `/planner/${dateForUrlFormatter(date)}/areas/${visit.plannerAreaId}/visits/${visit.id}`;

    if (visit.status === GqlPlannerVisitStatus.Unplanned && visit.rejectedAt !== null) {
        return <ProjectReviewVisitV2RejectionAlert visit={visit} />;
    }

    if (visit.status === GqlPlannerVisitStatus.Unplanned && visit.rejectedAt === null) {
        return <UnplannedAlert openInPlannerUrl={openInPlannerUrl} />;
    }

    if (visit.status === GqlPlannerVisitStatus.Planned) {
        return <PlannedAlert openInPlannerUrl={openInPlannerUrl} visit={visit} />;
    }

    if (visit.status === GqlPlannerVisitStatus.AwaitingReport) {
        return <AwaitingReportAlert visit={visit} />;
    }
}

function UnplannedAlert({ openInPlannerUrl }: { openInPlannerUrl: To }) {
    const { t } = useTranslation("project");

    return (
        <Alert severity="info">
            <Stack rowGap={1}>
                <Typography fontWeight="h6">{t("projectReviewVisitV2.status.unplanned.title")}</Typography>
                <Button variant="outlined" endIcon={<EndIconOpenInPlannerBtn />} component={Link} to={openInPlannerUrl}>
                    {t("projectReviewVisitV2.status.openInPlannerButton")}
                </Button>
            </Stack>
        </Alert>
    );
}

function PlannedAlert({ openInPlannerUrl, visit }: { openInPlannerUrl: To; visit: ProjectReviewVisitV2StatusHandlerFragment }) {
    const { t } = useTranslation("project");

    return (
        <Alert severity="info">
            <Stack rowGap={1}>
                <Typography>{t("projectReviewVisitV2.status.planned.title")}</Typography>
                <Typography variant="body2">
                    {t("projectReviewVisitV2.status.planned.scheduledFor", {
                        date: formatDate(parseStringToDate(visit.startTime as string)),
                    })}
                </Typography>
                <Button variant="outlined" endIcon={<EndIconOpenInPlannerBtn />} component={Link} to={openInPlannerUrl}>
                    {t("projectReviewVisitV2.status.openInPlannerButton")}
                </Button>
            </Stack>
        </Alert>
    );
}

function AwaitingReportAlert({ visit }: { visit: ProjectReviewVisitV2StatusHandlerFragment }) {
    const { t } = useTranslation("project");

    return (
        <Alert severity="info">
            <Stack rowGap={1}>
                <Typography>{t("projectReviewVisitV2.status.awaitingReport.title")}</Typography>
                <Typography variant="body2">
                    {t("projectReviewVisitV2.status.awaitingReport.description", {
                        date: formatDate(parseStringToDate(visit.startTime as string)),
                    })}
                </Typography>
                <Typography variant="body2">
                    {t("projectReviewVisitV2.status.awaitingReport.fieldworkersWithoutReportsLabel")}
                </Typography>
                <ProjectReviewVisitV2FieldworkersWithoutReportList visit={visit} />
            </Stack>
        </Alert>
    );
}
